import { Col, Row } from "antd";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { FormHelpSection } from "~components/Form/FormHelpSection";
import { FormInput } from "~components/Form/FormInput";
import { FormItem } from "~components/Form/FormItem";
import ManagePage from "~components/ManagePage/ManagePage";
import SelectCountryCode from "~components/SelectCountryCode/SelectCountryCode";
import SelectRegionType from "~components/SelectRegionType/SelectRegionType";
import { PermissionNames } from "~enums/permissions";
import type { LabelValue } from "~types/labelValue";
import { useLeagueService } from "../hooks/useLeagueService";
import type { UpsertLeaguePayload } from "../types/upsertLeague";
import formFieldsDescription from "./constants/formFieldsDescription.tsx";

const LeagueManagePage = () => {
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();
  const { league, isFetchLoading, upsertLeague, isUpsertPending: isSaving } = useLeagueService();
  const listUrl = `/${customerId}/admin/${sport}/${organizationId}/leagues`;

  const initialValues: Partial<UpsertLeaguePayload> | undefined = league
    ? {
        nameLocal: league.nameLocal,
        abbreviationLocal: league.abbreviationLocal ?? undefined,
        nameLatin: league.nameLatin ?? undefined,
        abbreviationLatin: league.abbreviationLatin ?? undefined,
        countryCode: league.countryCode ?? undefined,
        regionType: league.regionType,
      }
    : undefined;

  const goToLeagues = () => navigate(listUrl);
  const onCreateSuccess = (values: UpsertLeaguePayload) => navigate(listUrl, { state: { search: values.nameLocal } });
  const handleSubmit = (values: UpsertLeaguePayload) =>
    upsertLeague({
      ...values,
      leagueId: league?.leagueId,
    });

  return (
    <ManagePage<UpsertLeaguePayload>
      isLoading={isFetchLoading}
      isSaving={isSaving}
      entityType="league"
      permissionNames={[PermissionNames.LEAGUE]}
      entityName={league?.nameLocal}
      onBack={goToLeagues}
      onSubmit={handleSubmit}
      onFinalCreateSuccess={onCreateSuccess}
      initialValues={initialValues}
    >
      <Row>
        <Col span={16}>
          <FormHelpSection
            modalTitle={<FormattedMessage id="league.information" />}
            modalSubTitle={<FormattedMessage id="details" />}
            fields={formFieldsDescription.basic}
          />
        </Col>
      </Row>
      <Row gutter={[24, 18]}>
        <Col span={5}>
          <FormInput<UpsertLeaguePayload>
            name="nameLocal"
            showSkeleton={isFetchLoading}
            disabled={isSaving}
            required
            minLength={2}
            maxLength={150}
            noLeadingOrTrailingSpaces
          />
        </Col>
        <Col span={3}>
          <FormInput<UpsertLeaguePayload> name="abbreviationLocal" showSkeleton={isFetchLoading} disabled={isSaving} maxLength={30} />
        </Col>
        <Col span={5}>
          <FormInput<UpsertLeaguePayload> name="nameLatin" maxLength={150} showSkeleton={isFetchLoading} disabled={isSaving} />
        </Col>
        <Col span={3}>
          <FormInput<UpsertLeaguePayload> name="abbreviationLatin" showSkeleton={isFetchLoading} disabled={isSaving} />
        </Col>
        <Col span={8} />
        <Col span={8}>
          <FormItem<UpsertLeaguePayload>
            name="countryCode"
            showSkeleton={isFetchLoading}
            required
            getValueFromEvent={(option?: LabelValue) => option?.value}
          >
            <SelectCountryCode disabled={isSaving} />
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem<UpsertLeaguePayload>
            name="regionType"
            required
            showSkeleton={isFetchLoading}
            getValueFromEvent={(option?: LabelValue) => option?.value}
          >
            <SelectRegionType disabled={isSaving} />
          </FormItem>
        </Col>
      </Row>
    </ManagePage>
  );
};

export default LeagueManagePage;
